/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'preact/hooks';
import { Transition } from 'react-transition-group';
import { useInView } from 'react-intersection-observer';

import styleSheet from './components/styles/prediction.module.scss';
import styleSheetPoll from '../components/Polling/poll.module.scss';
import PollSponsor from '../components/Polling/pollSponsor.component';

import PredictionHeader from './components/predictionHeader';
import PredictionContent from './components/predictionContent';

import { getAuthHeader } from '../helpers/authentication';
import { sendView } from '../helpers/analytics';
import { applyLegacyEmbedColourSettings } from '../helpers/theme-config';

const styles = styleSheet.locals || {};

/**
 * Component for showing a Prediction
 *
 * @param {object} props -
 * @param {object} props.predictionData - prediction data
 * @param {object} props.embeddedFormData form data to embed after polling submit
 * @param {HTMLElement} props.container - embed container
 * @param {boolean} props.isPreview embed is a preview in admin UI
 * @param {boolean} props.colourOverrides - colour overrides
 * @returns {Element} <Prediction />
 */
function Prediction({
    predictionData,
    embeddedFormData,
    container,
    isPreview = false,
    colourOverrides
}) {
    const [startEmbedAnimation, setStartEmbedAnimation] = useState(false);

    const isGated = predictionData?.gated;
    const authHeader = getAuthHeader();

    const transitionStyles = {
        entered: { opacity: 1, transform: 'rotate(0deg)' }
    };

    const hasHeroImg = predictionData?.hero_image;

    /* eslint-disable no-magic-numbers */
    const largePrediction =
        predictionData?.questions?.[0]?.options.length > 15 || hasHeroImg;

    const { ref } = useInView({
        triggerOnce: true,
        threshold: largePrediction ? 0.1 : 0.5,
        onChange: (inView) => {
            if (inView) {
                setStartEmbedAnimation(true);

                if (!isPreview) {
                    sendView(
                        'PREDICTION',
                        predictionData?.prediction_id,
                        isGated,
                        authHeader
                    )
                        .then(() => {
                            globalThis.PLAYBOOK_CONFIG?.interactionEvent?.send(
                                'PREDICTION_VIEWED'
                            );
                        })
                        .catch((error) => {
                            // Handle the error here
                            console.error('Error sending view:', error);
                        });
                }
            }
        }
    });

    useEffect(() => {
        applyLegacyEmbedColourSettings(container, colourOverrides);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <style>
                {styleSheet.toString()}
                {styleSheetPoll.toString()}
            </style>
            <Transition nodeRef={ref} in={startEmbedAnimation}>
                {(transitionState) => (
                    <>
                        <div
                            data-cy="prediction-root"
                            ref={ref}
                            part="predictor-root"
                            className={styles.prediction}
                            style={{
                                maxWidth: predictionData.settings?.width || '',
                                ...transitionStyles[transitionState]
                            }}
                        >
                            <PredictionHeader prediction={predictionData} />

                            <div
                                className={styles.predictionContent}
                                part="predictor-content"
                            >
                                <PredictionContent
                                    prediction={predictionData}
                                    transitionState={transitionState}
                                    isPreview={isPreview}
                                    embeddedFormData={embeddedFormData}
                                    container={container}
                                />

                                {predictionData.sponsor_image_url && (
                                    <PollSponsor poll={predictionData} />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </Transition>
        </>
    );
}

Prediction.prototype = {
    predictionData: PropTypes.object.isRequired,
    embeddedFormData: PropTypes.object,
    container: PropTypes.node.isRequired,
    isPreview: PropTypes.bool
};

export default Prediction;
